import { useState } from "react";
import { Eye, GitCompare, History, RotateCcw } from "lucide-react";
import { formatDateTime, formatNumber, formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { FileChanges, type PageFileChange } from "@ncp/page-host";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Skeleton, cn, notifySaveFailed, notifySaved, useConfirm } from "@ncp/ui";
import { usePageActions, type PageDetail, type PageFile, type PageSummary } from "../api";

export const statusTone = (s: string) => (s === "Published" ? "success" : s === "Draft" ? "warning" : s === "InReview" ? "info" : "outline") as "success" | "warning" | "info" | "outline";
export const checkTone = (s?: string | null) => (s === "pass" ? "success" : s === "warn" ? "warning" : s === "fail" ? "danger" : "outline") as "success" | "warning" | "danger" | "outline";

/** The files that differ between two versions of a page. */
export function changesBetween(before: PageFile[], after: PageFile[]): PageFileChange[] {
  const a = new Map(before.map((f) => [f.path, f]));
  const b = new Map(after.map((f) => [f.path, f]));
  const out: PageFileChange[] = [];
  for (const [path, f] of b) {
    const old = a.get(path);
    if (!old) out.push({ path, change: "added", before: null, after: f.isBinary ? null : f.content, binary: f.isBinary });
    else if (old.content !== f.content) out.push({ path, change: "changed", before: old.isBinary ? null : old.content, after: f.isBinary ? null : f.content, binary: f.isBinary || old.isBinary });
  }
  for (const [path, f] of a) if (!b.has(path)) out.push({ path, change: "removed", before: f.isBinary ? null : f.content, after: null, binary: f.isBinary });
  return out.sort((x, y) => x.path.localeCompare(y.path));
}

/** Every version of the page: open one read-only, compare it with the newest, or bring its files back into the draft. */
export function VersionsTab({ detail, viewing, onView, onRestored }: { detail: PageDetail; viewing: string | null; onView: (id: string | null) => void; onRestored: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const confirm = useConfirm();
  const actions = usePageActions();
  const newest = detail.versions[0];
  const [compare, setCompare] = useState<{ from: PageSummary; changes: PageFileChange[] | null } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const openCompare = async (v: PageSummary) => {
    if (!newest) return;
    setCompare({ from: v, changes: null });
    try {
      const [a, b] = await Promise.all([actions.exportJson(v.id), actions.exportJson(newest.id)]);
      setCompare({ from: v, changes: changesBetween(a.files, b.files) });
    } catch (e) { notifySaveFailed(e, t("admin:common.error")); setCompare(null); }
  };

  const restore = async (v: PageSummary) => {
    if (!(await confirm({ title: t("admin:pageStudio.versions.restoreTitle", { version: v.version }), description: t("admin:pageStudio.versions.restoreBody"), confirmLabel: t("admin:pageStudio.versions.restore"), irreversible: false }))) return;
    setBusy(v.id);
    // Reading a version is not a save of its own: its failure is reported here, the saves report theirs (useSave).
    const read = (versionId: string) => actions.exportJson(versionId).catch((e: unknown) => { notifySaveFailed(e); throw e; });
    try {
      const old = await read(v.id);
      const draft = newest?.status === "Draft" ? newest : (await actions.draft.mutateAsync(detail.page.key)).page;
      const now = await read(draft.id);
      const keep = new Set(old.files.map((f) => f.path));
      const saved = await actions.save.mutateAsync({ id: draft.id, patch: {
        upserts: old.files.map((f) => ({ path: f.path, content: f.content, isBinary: f.isBinary })),
        deletes: now.files.filter((f) => !keep.has(f.path)).map((f) => f.path),
        changeNote: t("admin:pageStudio.versions.restoreNote", { version: v.version }),
      } });
      notifySaved(saved, t("admin:pageStudio.versions.restored", { version: v.version, draft: draft.version }));
      onView(null);
      onRestored();
    } catch { /* reported above or by useSave */ } finally { setBusy(null); }
  };

  return (
    <div className="rounded-xl border border-sand-200 bg-white">
      <ul className="divide-y divide-sand-100">
        {detail.versions.map((v) => (
          <li key={v.id} className={cn("flex flex-wrap items-center gap-3 px-4 py-3", (viewing ? viewing === v.id : v.id === newest?.id) && "bg-aegold-50/40")}>
            <History className="size-4 text-sand-500" />
            <span className="w-10 font-semibold tabular">v{v.version}</span>
            <Badge size="sm" variant={statusTone(v.status)}>{t(`admin:pageStudio.status.${v.status}`, { defaultValue: v.status })}</Badge>
            {v.validationStatus ? <Badge size="sm" variant={checkTone(v.validationStatus)}>{t(`admin:pageStudio.check.${v.validationStatus}`, { defaultValue: v.validationStatus })}</Badge> : null}
            <span className="text-xs text-muted-foreground">
              {v.publishedAt ? t("admin:pageStudio.versions.published", { when: formatDateTime(v.publishedAt, locale) }) : t("admin:pageStudio.versions.updated", { when: formatRelative(v.updatedAt, locale) })}
              {` · ${t("admin:pageStudio.versions.files", { count: v.fileCount })} · ${formatNumber(Math.round(v.sizeBytes / 1024), locale)} KB`}
              {v.origin && v.origin !== "studio" ? ` · ${v.origin.startsWith("repo:") ? t("admin:pageStudio.origin.repo", { folder: v.origin.slice(5) }) : t(`admin:pageStudio.origin.${v.origin}`, { defaultValue: v.origin })}` : ""}
            </span>
            <span className="ms-auto flex gap-1">
              {v.id !== newest?.id ? (<>
                <Button size="sm" variant="ghost" onClick={() => onView(viewing === v.id ? null : v.id)}><Eye /> {viewing === v.id ? t("admin:pageStudio.versions.backToNewest") : t("admin:common.open")}</Button>
                <Button size="sm" variant="ghost" onClick={() => void openCompare(v)}><GitCompare /> {t("admin:pageStudio.versions.compare")}</Button>
                <Button size="sm" variant="ghost" loading={busy === v.id} onClick={() => void restore(v)}><RotateCcw /> {t("admin:pageStudio.versions.restore")}</Button>
              </>) : <span className="text-xs text-muted-foreground">{t("admin:pageStudio.versions.newest")}</span>}
            </span>
          </li>
        ))}
      </ul>
      <Dialog open={!!compare} onOpenChange={(o) => !o && setCompare(null)}>
        <DialogContent size="xl">
          <DialogHeader>
            <DialogTitle>{compare ? t("admin:pageStudio.versions.compareTitle", { from: compare.from.version, to: newest?.version }) : ""}</DialogTitle>
            <DialogDescription>{t("admin:pageStudio.versions.compareHint")}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-auto">{compare?.changes ? <FileChanges changes={compare.changes} /> : <Skeleton className="h-40" />}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
